/**
  * @collapsed/react v4.0.1
  *
  * Copyright (c) 2019-2023, Rogin Farrer
  *
  * This source code is licensed under the MIT license found in the
  * LICENSE.md file in the root directory of this source tree.
  *
  * @license MIT
  */


// src/useCollapse.ts
import * as React2 from "react";
import { Collapse } from "@collapsed/core";

// src/utils.ts
import * as React from "react";
import { useState, useRef, useEffect, useCallback } from "react";
import warning from "tiny-warning";
var callAll = (...fns) => (...args) => fns.forEach((fn) => fn && fn(...args));
function assignRef(ref, value) {
  if (ref == null)
    return;
  if (typeof ref === "function") {
    ref(value);
  } else {
    try {
      ref.current = value;
    } catch (error) {
      throw new Error(`Cannot assign value "${value}" to ref "${ref}"`);
    }
  }
}
function mergeRefs(...refs) {
  if (refs.every((ref) => ref == null)) {
    return null;
  }
  return (node) => {
    refs.forEach((ref) => {
      assignRef(ref, node);
    });
  };
}
function useEvent(callback) {
  const ref = useRef(callback);
  useEffect(() => {
    ref.current = callback;
  });
  return useCallback((...args) => ref.current?.(...args), []);
}
function useControlledState(value, defaultValue, callback) {
  const [state, setState] = useState(defaultValue);
  const initiallyControlled = useRef(typeof value !== "undefined");
  const effectiveValue = initiallyControlled.current ? value : state;
  const cb = useEvent(callback);
  const onChange = useCallback(
    (update) => {
      const setter = update;
      const newValue = typeof update === "function" ? setter(effectiveValue) : update;
      if (!initiallyControlled.current) {
        setState(newValue);
      }
      cb?.(newValue);
    },
    [cb, effectiveValue]
  );
  useEffect(() => {
    warning(
      !(initiallyControlled.current && value == null),
      "useCollapse is changing from controlled to uncontrolled. useCollapse should not switch from controlled to uncontrolled (or vice versa). Decide between using a controlled or uncontrolled collapse for the lifetime of the component. Check the `isExpanded` prop."
    );
    warning(
      !(!initiallyControlled.current && value != null),
      "useCollapse is changing from uncontrolled to controlled. useCollapse should not switch from uncontrolled to controlled (or vice versa). Decide between using a controlled or uncontrolled collapse for the lifetime of the component. Check the `isExpanded` prop."
    );
  }, [value]);
  return [effectiveValue, onChange];
}
var useLayoutEffect2 = typeof window !== "undefined" ? React.useLayoutEffect : useEffect;

// src/useCollapse.ts
function useCollapse(options = {}) {
  const {
    isExpanded: propExpanded,
    defaultExpanded: propDefaultExpanded,
    onExpandedChange,
    ...opts
  } = options;
  const id = React2.useId();
  const collapseEl = React2.useRef(null);
  const [toggleEl, setToggleEl] = React2.useState(null);
  const [isExpanded, setExpanded] = useControlledState(
    propExpanded,
    propDefaultExpanded,
    onExpandedChange
  );
  const resolvedOptions = {
    id,
    ...opts,
    getCollapseElement: () => collapseEl.current,
    getToggleElement: () => toggleEl,
    defaultExpanded: isExpanded,
    onExpandedChange: setExpanded
  };
  const [instance] = React2.useState(() => new Collapse(resolvedOptions));
  instance.setOptions(resolvedOptions);
  useLayoutEffect2(() => {
    if (isExpanded) {
      instance.open();
    } else {
      instance.close();
    }
  }, [isExpanded, instance]);
  const assignRef2 = (node) => {
    if (collapseEl.current !== node) {
      collapseEl.current = node;
      if (!!node) {
        instance.init();
      }
    }
  };
  return {
    getCollapseProps({
      refKey = "ref",
      onTransitionEnd = () => {
      },
      ...rest
    } = {}) {
      const theirRef = rest[refKey];
      if (!instance) {
        return { [refKey]: mergeRefs(theirRef, assignRef2) };
      }
      const { onTransitionEndHandler, ...props } = instance.getCollapse();
      return {
        ...rest,
        ...props,
        [refKey]: mergeRefs(theirRef, assignRef2),
        onTransitionEnd: callAll(
          onTransitionEndHandler,
          onTransitionEnd
        )
      };
    },
    getToggleProps({
      disabled,
      onClick = () => {
      },
      refKey = "ref",
      ...rest
    } = {}) {
      const theirRef = rest[refKey];
      if (!instance) {
        return { [refKey]: mergeRefs(theirRef, setToggleEl) };
      }
      const { onClickHandler, ...props } = instance.getToggle({
        disabled
      });
      return {
        ...rest,
        ...props,
        [refKey]: mergeRefs(theirRef, setToggleEl),
        onClick: callAll(onClickHandler, onClick)
      };
    },
    isExpanded,
    setExpanded
  };
}
export {
  useCollapse
};
